import { API_ENDPOINTS } from "@/constants/api";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TicketCategory = "billing" | "technical" | "feature_request" | "general";
export type TicketPriority = "low" | "medium" | "high" | "urgent";
export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

export interface TicketMessage {
  id: string;
  ticketId: string;
  sender: "customer" | "admin";
  senderName: string;
  content: string;
  isInternal: boolean;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  ticketNumber: number;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  assignedTo: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TicketDetail extends SupportTicket {
  messages: TicketMessage[];
}

export interface PaginatedTickets {
  data: SupportTicket[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const SupportService = {
  getTickets: async (page = 1, limit = 20): Promise<PaginatedTickets> => {
    const url = new URL(API_ENDPOINTS.SUPPORT.TICKETS, window.location.origin);
    url.searchParams.set("page", String(page));
    url.searchParams.set("limit", String(limit));
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) throw new Error("Failed to fetch tickets");
    return res.json();
  },

  getTicketById: async (ticketId: string): Promise<TicketDetail> => {
    const res = await fetch(API_ENDPOINTS.SUPPORT.TICKET_BY_ID(ticketId), {
      credentials: "include",
    });
    if (res.status === 404) throw new Error("Ticket not found");
    if (!res.ok) throw new Error("Failed to fetch ticket");
    return res.json();
  },

  createTicket: async (data: {
    subject: string;
    description: string;
    category: TicketCategory;
    priority: TicketPriority;
  }): Promise<SupportTicket> => {
    const res = await fetch(API_ENDPOINTS.SUPPORT.TICKETS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error || "Failed to create ticket");
    }
    return res.json();
  },

  addMessage: async (ticketId: string, content: string): Promise<TicketMessage> => {
    const res = await fetch(API_ENDPOINTS.SUPPORT.MESSAGES(ticketId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ content }),
    });
    if (!res.ok) throw new Error("Failed to send message");
    return res.json();
  },
};
